// Procedural General-MIDI drum kits (channel-10 percussion). Each GM percussion
// note triggers a synthesized voice; a `kit` id selects the character —
// acoustic, TR-808, TR-909, or boom-bap (lo-fi hip-hop). Pure Web Audio (the
// classic machines were analog synths, so this captures their sound with no
// samples/network); works live and in offline render. Implements Instrument;
// notes are one-shots (noteOff is a no-op).
import { Instrument } from "./instrument";

/** GM percussion key → short label (for piano-roll row labels / audition pads). */
export const GM_DRUM_LABELS: Record<number, string> = {
  35: "Ac Kick", 36: "Kick", 37: "Rimshot", 38: "Snare", 39: "Clap", 40: "El Snare",
  41: "Lo Floor Tom", 42: "Closed HH", 43: "Hi Floor Tom", 44: "Pedal HH", 45: "Low Tom",
  46: "Open HH", 47: "LoMid Tom", 48: "HiMid Tom", 49: "Crash", 50: "High Tom", 51: "Ride",
  52: "China", 53: "Ride Bell", 54: "Tambourine", 55: "Splash", 56: "Cowbell", 57: "Crash 2",
  58: "Vibraslap", 59: "Ride 2", 60: "Hi Bongo", 61: "Low Bongo", 62: "Mute Conga",
  63: "Open Conga", 64: "Low Conga", 65: "Hi Timbale", 66: "Lo Timbale", 67: "Hi Agogo",
  68: "Lo Agogo", 69: "Cabasa", 70: "Maracas", 71: "Sh Whistle", 72: "Lg Whistle",
  73: "Sh Guiro", 74: "Lg Guiro", 75: "Claves", 76: "Hi Woodblk", 77: "Lo Woodblk",
  78: "Mute Cuica", 79: "Open Cuica", 80: "Mute Tri", 81: "Open Tri",
};

export interface KitCfg {
  kickBase: number; kickPitch: number; kickDecay: number; kickClick: boolean;
  snareTone: number; snareNoise: number; snareBright: boolean;
  hatClosed: number; hatOpen: number; hatMetal: boolean;
  lofi: boolean; lofiCutoff?: number;
}
export const DRUM_KITS: Record<string, { label: string; cfg: KitCfg }> = {
  acoustic: { label: "Acoustic", cfg: { kickBase: 48, kickPitch: 150, kickDecay: 0.35, kickClick: false, snareTone: 190, snareNoise: 0.18, snareBright: false, hatClosed: 0.06, hatOpen: 0.32, hatMetal: false, lofi: false } },
  tr808:    { label: "TR-808",   cfg: { kickBase: 42, kickPitch: 120, kickDecay: 0.95, kickClick: false, snareTone: 180, snareNoise: 0.12, snareBright: false, hatClosed: 0.05, hatOpen: 0.42, hatMetal: true,  lofi: false } },
  tr909:    { label: "TR-909",   cfg: { kickBase: 50, kickPitch: 170, kickDecay: 0.34, kickClick: true,  snareTone: 205, snareNoise: 0.22, snareBright: true,  hatClosed: 0.05, hatOpen: 0.30, hatMetal: false, lofi: false } },
  boombap:  { label: "Boom-Bap (Lo-Fi)", cfg: { kickBase: 55, kickPitch: 130, kickDecay: 0.26, kickClick: false, snareTone: 215, snareNoise: 0.2, snareBright: true, hatClosed: 0.04, hatOpen: 0.24, hatMetal: false, lofi: true } },
  trap:     { label: "Trap (808 sub)", cfg: { kickBase: 36, kickPitch: 140, kickDecay: 1.30, kickClick: false, snareTone: 240, snareNoise: 0.08, snareBright: true,  hatClosed: 0.03, hatOpen: 0.55, hatMetal: true,  lofi: false } },
  techno:   { label: "Techno (hard)",  cfg: { kickBase: 50, kickPitch: 210, kickDecay: 0.45, kickClick: true,  snareTone: 200, snareNoise: 0.14, snareBright: true,  hatClosed: 0.06, hatOpen: 0.35, hatMetal: false, lofi: false } },
  electro:  { label: "Electro (707)",  cfg: { kickBase: 56, kickPitch: 165, kickDecay: 0.22, kickClick: true,  snareTone: 260, snareNoise: 0.13, snareBright: true,  hatClosed: 0.04, hatOpen: 0.28, hatMetal: true,  lofi: false } },
  rock:     { label: "Rock (big)",     cfg: { kickBase: 60, kickPitch: 185, kickDecay: 0.30, kickClick: false, snareTone: 200, snareNoise: 0.26, snareBright: false, hatClosed: 0.08, hatOpen: 0.42, hatMetal: false, lofi: false } },
  vinyl:    { label: "Vinyl (dusty)",  cfg: { kickBase: 52, kickPitch: 120, kickDecay: 0.28, kickClick: false, snareTone: 200, snareNoise: 0.18, snareBright: false, hatClosed: 0.04, hatOpen: 0.22, hatMetal: false, lofi: true, lofiCutoff: 4200 } },
};

export class DrumKit implements Instrument {
  output: GainNode;
  private bus: AudioNode;         // voices connect here (lofi lowpass → output)
  private cfg: KitCfg;
  private noise: AudioBuffer;
  private active = new Set<AudioScheduledSourceNode>();

  constructor(private ctx: BaseAudioContext, public kit = "acoustic") {
    this.cfg = (DRUM_KITS[kit] ?? DRUM_KITS.acoustic).cfg;
    this.output = ctx.createGain();
    if (this.cfg.lofi) { const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = this.cfg.lofiCutoff ?? 7000; lp.Q.value = 0.7; lp.connect(this.output); this.bus = lp; }
    else this.bus = this.output;
    const n = Math.floor(ctx.sampleRate);
    this.noise = ctx.createBuffer(1, n, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
  }

  noteOn(midi: number, vel = 0.9) { this.hit(midi, this.ctx.currentTime, vel); }
  noteOff() { /* one-shots ring out */ }
  scheduleNote(midi: number, vel: number, start: number) { this.hit(midi, start, vel); }
  allNotesOff() { for (const s of this.active) { try { s.stop(); } catch {} } this.active.clear(); }
  dispose() { this.allNotesOff(); try { this.output.disconnect(); } catch {} }

  // ---- node helpers ----
  private track<T extends AudioScheduledSourceNode>(n: T): T { this.active.add(n); n.onended = () => this.active.delete(n); return n; }
  private osc(type: OscillatorType) { const o = this.ctx.createOscillator(); o.type = type; return this.track(o); }
  private noiseSrc() { const s = this.ctx.createBufferSource(); s.buffer = this.noise; s.loop = true; return this.track(s); }
  private gain(v = 0) { const g = this.ctx.createGain(); g.gain.value = v; return g; }
  private filt(type: BiquadFilterType, freq: number, q = 0.7) { const f = this.ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q; return f; }
  private decayTo0(g: GainNode, when: number, peak: number, dur: number) {
    g.gain.setValueAtTime(Math.max(0.0001, peak), when);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  }

  // ---- voices (kit-parameterised) ----
  private kick(when: number, vel: number, mult = 1) {
    const c = this.cfg, o = this.osc("sine"), g = this.gain();
    o.frequency.setValueAtTime(c.kickPitch, when);
    o.frequency.exponentialRampToValueAtTime(c.kickBase * mult, when + (c.kickDecay > 0.5 ? 0.15 : 0.08));
    this.decayTo0(g, when, vel, c.kickDecay);
    o.connect(g).connect(this.bus); o.start(when); o.stop(when + c.kickDecay + 0.05);
    if (c.kickClick) { const n = this.noiseSrc(), hp = this.filt("highpass", 3000), cg = this.gain(); this.decayTo0(cg, when, vel * 0.5, 0.02); n.connect(hp).connect(cg).connect(this.bus); n.start(when); n.stop(when + 0.03); }
  }
  private snare(when: number, vel: number, elec = false) {
    const c = this.cfg;
    const o = this.osc("triangle"), og = this.gain();
    o.frequency.setValueAtTime(elec || c.snareBright ? c.snareTone * 1.6 : c.snareTone, when);
    this.decayTo0(og, when, vel * 0.5, 0.12);
    o.connect(og).connect(this.bus); o.start(when); o.stop(when + 0.15);
    const n = this.noiseSrc(), hp = this.filt("highpass", c.snareBright ? 1800 : 1400), ng = this.gain();
    this.decayTo0(ng, when, vel * 0.7, c.snareNoise);
    n.connect(hp).connect(ng).connect(this.bus); n.start(when); n.stop(when + c.snareNoise + 0.05);
  }
  private hat(when: number, vel: number, open: boolean) {
    const dur = open ? this.cfg.hatOpen : this.cfg.hatClosed;
    if (this.cfg.hatMetal) {
      const hp = this.filt("highpass", 6000), bp = this.filt("bandpass", 9000, 1.5), g = this.gain();
      this.decayTo0(g, when, vel * 0.4, dur);
      for (const fr of [800, 1090, 1490, 1980, 2470, 3200]) { const o = this.osc("square"); o.frequency.setValueAtTime(fr, when); o.connect(hp); o.start(when); o.stop(when + dur + 0.02); }
      hp.connect(bp).connect(g).connect(this.bus);
    } else {
      const n = this.noiseSrc(), hp = this.filt("highpass", 7000), bp = this.filt("bandpass", 10000, 1.2), g = this.gain();
      this.decayTo0(g, when, vel * 0.5, dur);
      n.connect(hp).connect(bp).connect(g).connect(this.bus); n.start(when); n.stop(when + dur + 0.03);
    }
  }
  private tom(when: number, vel: number, freq: number) {
    const o = this.osc("sine"), g = this.gain();
    o.frequency.setValueAtTime(freq * 1.6, when);
    o.frequency.exponentialRampToValueAtTime(freq, when + 0.1);
    this.decayTo0(g, when, vel, 0.4);
    o.connect(g).connect(this.bus); o.start(when); o.stop(when + 0.45);
  }
  private clap(when: number, vel: number) {
    const n = this.noiseSrc(), bp = this.filt("bandpass", 1200, 0.8), g = this.gain();
    g.gain.setValueAtTime(0.0001, when);
    for (const t of [0, 0.011, 0.022]) { g.gain.setValueAtTime(vel * 0.7, when + t); g.gain.exponentialRampToValueAtTime(0.0001, when + t + 0.02); }
    g.gain.setValueAtTime(vel * 0.7, when + 0.033); g.gain.exponentialRampToValueAtTime(0.0001, when + 0.16);
    n.connect(bp).connect(g).connect(this.bus); n.start(when); n.stop(when + 0.2);
  }
  private cymbal(when: number, vel: number, dur: number, hp: number) {
    const n = this.noiseSrc(), f = this.filt("highpass", hp), g = this.gain();
    this.decayTo0(g, when, vel * 0.35, dur);
    n.connect(f).connect(g).connect(this.bus); n.start(when); n.stop(when + dur + 0.05);
  }
  private metal(when: number, vel: number, freqs: number[], dur: number) {
    const hp = this.filt("highpass", 500), g = this.gain();
    this.decayTo0(g, when, vel * 0.4, dur);
    for (const fr of freqs) { const o = this.osc("square"); o.frequency.setValueAtTime(fr, when); o.connect(hp); o.start(when); o.stop(when + dur + 0.02); }
    hp.connect(g).connect(this.bus);
  }
  private ping(when: number, vel: number, freq: number, dur = 0.05, type: OscillatorType = "triangle") {
    const o = this.osc(type), g = this.gain();
    o.frequency.setValueAtTime(freq, when);
    this.decayTo0(g, when, vel * 0.6, dur);
    o.connect(g).connect(this.bus); o.start(when); o.stop(when + dur + 0.02);
  }
  private shaker(when: number, vel: number, dur = 0.06) {
    const n = this.noiseSrc(), hp = this.filt("highpass", 5000), g = this.gain();
    this.decayTo0(g, when, vel * 0.4, dur);
    n.connect(hp).connect(g).connect(this.bus); n.start(when); n.stop(when + dur + 0.02);
  }

  private hit(midi: number, when: number, vel: number) {
    switch (midi) {
      case 35: return this.kick(when, vel, 0.9);
      case 36: return this.kick(when, vel, 1.0);
      case 37: return this.ping(when, vel, 400, 0.04, "square"); // rimshot/side stick
      case 38: return this.snare(when, vel, false);
      case 40: return this.snare(when, vel, true);
      case 39: return this.clap(when, vel);
      case 41: return this.tom(when, vel, 82);
      case 43: return this.tom(when, vel, 98);
      case 45: return this.tom(when, vel, 116);
      case 47: return this.tom(when, vel, 138);
      case 48: return this.tom(when, vel, 165);
      case 50: return this.tom(when, vel, 196);
      case 42: case 44: return this.hat(when, vel, false);
      case 46: return this.hat(when, vel, true);
      case 49: case 57: return this.cymbal(when, vel, 1.2, 5000);
      case 55: return this.cymbal(when, vel, 0.5, 6000); // splash
      case 52: return this.cymbal(when, vel, 1.0, 4000); // china
      case 51: case 59: return this.cymbal(when, vel, 0.9, 7000); // ride
      case 53: return this.metal(when, vel, [1200, 1700], 0.5); // ride bell
      case 56: return this.metal(when, vel, [540, 800], 0.25); // cowbell
      case 67: return this.metal(when, vel, [1400, 2100], 0.2); // hi agogo
      case 68: return this.metal(when, vel, [1000, 1500], 0.2); // lo agogo
      case 54: return this.shaker(when, vel, 0.12); // tambourine
      case 58: return this.shaker(when, vel, 0.2);  // vibraslap
      case 69: case 70: return this.shaker(when, vel, 0.06); // cabasa/maracas
      case 73: case 74: return this.shaker(when, vel, midi === 74 ? 0.14 : 0.05); // guiro
      case 60: return this.tom(when, vel, 260); // hi bongo
      case 61: return this.tom(when, vel, 200); // low bongo
      case 62: return this.tom(when, vel * 0.8, 190); // mute conga
      case 63: return this.tom(when, vel, 220); // open conga
      case 64: return this.tom(when, vel, 160); // low conga
      case 65: return this.tom(when, vel, 300); // hi timbale
      case 66: return this.tom(when, vel, 240); // lo timbale
      case 75: return this.ping(when, vel, 2500, 0.03, "triangle"); // claves
      case 76: return this.ping(when, vel, 1000, 0.04); // hi woodblock
      case 77: return this.ping(when, vel, 800, 0.04);  // lo woodblock
      case 80: return this.ping(when, vel, 5000, 0.4, "sine"); // mute triangle
      case 81: return this.ping(when, vel, 5000, 0.9, "sine"); // open triangle
      case 71: case 72: return this.ping(when, vel, 2100, midi === 72 ? 0.3 : 0.12, "sine"); // whistle
      case 78: return this.tom(when, vel * 0.7, 400); // mute cuica
      case 79: return this.tom(when, vel, 500);       // open cuica
      default: return this.ping(when, vel * 0.6, 900, 0.05); // generic click
    }
  }
}
